/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';

import {
    CartLine,
    SimpleProduct,
    AsyncResult,
    ProductSearchResult,
    ProductPrice,
    ProductWarehouseInventoryAvailability,
    AttributeValue,
    SalesOrder
} from '@msdyn365-commerce/retail-proxy';

import { getOrderHistoryAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CustomersDataActions.g';
import {
    getAttributeValuesAsync,
    getByIdAsync,
    getEstimatedAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { getTelemetryObject, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';

import {
    GetSelectedQuantityValuesInput,
    IGetSelectedQuantityStateData
} from '../../actions/BuyBoxSelectedQuantity/ecomm-buybox-selected-quantity';
import {
    getByCustomerBTGPTransactionAsync,
    getAllBTGPCartLineAsync,
    checkAvailableSerialAsync,
    getEventAvailabilityAsync,
    getProductEventsByIdAsync
} from '../../actions/DataActionExtension.g';

import { v4 as uuidv4 } from 'uuid';

import { getProductServiceObjects } from './component/ecomm-form-get-service-objects';

import resource from './resources/resource.json';

import { InventoryLvls } from '../../components/enums';

import getFormJSON from './utilities/getFormJSON';

import {
    IEcommProductDetailsFormData,
    Attributes,
    IField,
    IFormStructure,
    IdState,
    IEvent,
    IEventDetails
} from './ecomm-product-details-form.data';
import { IEcommProductDetailsFormProps } from './ecomm-product-details-form.props.autogenerated';

import moment from 'moment';

export interface IEcommProductDetailsFormViewProps extends IEcommProductDetailsFormProps<IEcommProductDetailsFormData> {
    telemetryContent: ITelemetryContent;
    productsList: ProductSearchResult[];
    simpleProducts: SimpleProduct[];
    productsIds: number[];
    productPriceList: ProductPrice[];
    attributesList: AttributeValue[];
    productAvailabilities: ProductWarehouseInventoryAvailability[];
    formScheme: IField[];
    formsHeaders: string[];
    formName: string;
    selectedProduct: AsyncResult<SimpleProduct>;
    subProduct: SimpleProduct;
    hasSerial: boolean;
    registrationClosed: boolean;
    itemUnavailable: boolean;
    eventAvailable: boolean;
    rowsData: IdState[];
    matchingCartItems: [];
    addRow(noOfRows: number): void;
    replaceRow(noOfRows: number, idx: number[]): void;
    removeRow(idx: number): void;
    removeMultipleRows(ix: number): void;
    retrieveRow(input: any[]): void;
    isCustomPrice: boolean;
    isEditMode: boolean;
    eventProduct: boolean;
    eventId: string;
    eventRecords: IEvent[];
    eventMinCapacityThreshold: number;
    eventDetails: IEventDetails[];
    allowMultipleRegistrations: boolean;
    showRemarksField: boolean;
    insuranceAmount: string;
    insuranceFeeLabel: string;
    makeChineseNameMandatory: boolean;
}

/**
 *
 * EcommProductDetailsForm component
 * @extends {React.PureComponent<IEcommProductDetailsFormProps<IEcommProductDetailsFormData>>}
 */
class EcommProductDetailsForm extends React.PureComponent<IEcommProductDetailsFormProps<IEcommProductDetailsFormData>> {
    public tableHeaderArray: string[] = [];

    private telemetryContent: ITelemetryContent;
    private localeCode = this.props.context.request.locale.split('-')[0];
    public state: any;

    constructor(props: IEcommProductDetailsFormProps<IEcommProductDetailsFormData>) {
        super(props);

        this.state = {
            selectedProduct: {},
            addRow: {},
            replaceRow: {},
            removeRow: {},
            removeMultipleRows: {},
            retrieveRow: {},
            headerArrayState: [],
            formStructState: [],
            formName: '',
            rowsData: [{ id: uuidv4() }],
            productResult: [],
            simpleProductResult: [],
            productsIds: [],
            productPriceArr: [],
            attributesList: [],
            productsAvailability: {},
            matchingCartItems: [],
            hasSerial: false,
            itemUnavailable: false,
            isEditMode: false,
            eventProduct: true,
            eventAvailable: false,
            eventRecords: [],
            eventMinCapacityThreshold: 0,
            eventDetails: [],
            allowMultipleRegistrations: true,
            showRemarksField: true,
            insuranceAmount: '0',
            insuranceFeeLabel: '',
            makeChineseNameMandatory: true,
            registrationClosedApiLoaded: false,
            eventAvailableApiLoaded: false,
            itemUnavailableSerialApiLoaded: false,
            itemUnavailableStockApiLoaded: false
        };

        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    private delay(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    public async componentDidMount(): Promise<void> {
        const {
            data: { categories, cart, product, selectedQuantity }
        } = this.props;

        let formStructure: IFormStructure;

        if (!categories.result) {
            await this.delay(300);
        }

        const categoryId = categories.result!.filter(category => category.ParentCategory === 0)[0].RecordId;

        const resultObj = await getProductServiceObjects(this.props, categoryId).catch(error => {
            console.log('[product-list] error', error);
            if (
                this.state['productResult']?.length > 0 ||
                this.state['simpleProductResult']?.length > 0 ||
                this.state['productsIds']?.length > 0 ||
                this.state['productPriceArr']?.length > 0 ||
                this.state['attributesList']?.length > 0
            ) {
                this.setState({
                    productResult: [],
                    simpleProductResult: [],
                    productsAvailability: {},
                    productsIds: [],
                    productPriceArr: [],
                    attributesList: []
                });
            }
        });

        let productList: ProductSearchResult[] = [];
        let simpleProductList: SimpleProduct[] | undefined = [];
        let productsIds: number[] | undefined = [];
        let productPriceList: ProductPrice[] | undefined = [];
        let productsAvailability: ProductWarehouseInventoryAvailability[] | undefined = [];
        let attributeList: AttributeValue[] | undefined = [];

        if (resultObj && resultObj.selectedProducts) {
            productList = resultObj.selectedProducts;
            simpleProductList = resultObj.productsSimpleDetails;
            productPriceList = resultObj.activePrices;
            productsIds = resultObj.productIds;
            productsAvailability = resultObj.productsAvailability;
            attributeList = resultObj.attributesList;
        }

        if (JSON.stringify(productList) !== JSON.stringify(this.state['productResult'])) {
            this.setState({
                simpleProductResult: simpleProductList,
                productsAvailability: productsAvailability,
                productsIds: productsIds,
                productPriceArr: productPriceList,
                attributesList: attributeList,
                productResult: productList
            });
        }

        let matchingItemsLength = 1;

        const itemArray = [];
        let eventProduct = true;

        const parameters = window.location.search;
        const cartlineId = new URLSearchParams(parameters).get('cartlineId');

        // Unable to pass populated array of either cart items/ past transactions to view file.
        // This portion of the code is replicated in view to ensure that formItem array is not empty.
        // This portion of the code in controller is meant for buildFormStructure.
        // Check state matchingCartItems array again.
        if (cartlineId && cart.result) {
            const cartLines: CartLine[] = cart.result.cart.CartLines!;
            const found = cartLines.find(cartline => cartline.LineId === cartlineId);

            const cartLineData: any[] = [];

            const getAllCartLineResult = await getAllBTGPCartLineAsync(
                { callerContext: this.props.context.actionContext },
                (await cart).cart.Id
            );

            if (getAllCartLineResult.length > 0) {
                getAllCartLineResult.map(record => {
                    cartLineData.push(JSON.parse(record));
                });
            }

            const cartLineIndex = cartLineData.findIndex(cl => cl['CartLineID'] === cartlineId);

            if (found && cartLineIndex >= 0) {
                const childCartLines = cartLineData.filter(cl => cl['ParentCartLineID'] === cartlineId);

                let eventNames = cartLineData[`${cartLineIndex}`]['EventName'];
                let eventIds = cartLineData[`${cartLineIndex}`]['EventID'];
                let quantity = found.Quantity ? found.Quantity : 1;
                let subProductQuantity = 0;

                childCartLines.map(child => {
                    quantity++;
                    eventNames += `;${child['EventName']}`;
                    eventIds += `;${child['EventID']}`;

                    if (child['ProductID'] !== String(found.ProductId)) {
                        const subProductfound = cartLines.find(cartline => cartline.LineId === child['CartLineID']);
                        if (subProductfound) {
                            subProductQuantity = subProductfound.Quantity!;
                        }
                    }
                });

                itemArray.push({
                    comment: found.Comment ? found.Comment : '',
                    quantity: quantity,
                    names: [
                        cartLineData[`${cartLineIndex}`]['Name_1'],
                        cartLineData[`${cartLineIndex}`]['Name_2'],
                        cartLineData[`${cartLineIndex}`]['Name_3'],
                        cartLineData[`${cartLineIndex}`]['Name_4'],
                        cartLineData[`${cartLineIndex}`]['Name_5'],
                        cartLineData[`${cartLineIndex}`]['Name_6'],
                        cartLineData[`${cartLineIndex}`]['Name_7'],
                        cartLineData[`${cartLineIndex}`]['Name_8'],
                        cartLineData[`${cartLineIndex}`]['Name_9'],
                        cartLineData[`${cartLineIndex}`]['Name_10'],
                        cartLineData[`${cartLineIndex}`]['Name_11'],
                        cartLineData[`${cartLineIndex}`]['Name_12'],
                        cartLineData[`${cartLineIndex}`]['Name_13'],
                        cartLineData[`${cartLineIndex}`]['Name_14'],
                        cartLineData[`${cartLineIndex}`]['Name_15'],
                        cartLineData[`${cartLineIndex}`]['Name_16'],
                        cartLineData[`${cartLineIndex}`]['Name_17'],
                        cartLineData[`${cartLineIndex}`]['Name_18'],
                        cartLineData[`${cartLineIndex}`]['Name_19'],
                        cartLineData[`${cartLineIndex}`]['Name_20']
                    ],
                    category: cartLineData[`${cartLineIndex}`]['Category'],
                    eventID: eventIds,
                    eventName: eventNames,
                    remarks: cartLineData[`${cartLineIndex}`]['Remarks'],
                    message: cartLineData[`${cartLineIndex}`]['Message'],
                    dedicatedBy: cartLineData[`${cartLineIndex}`]['DedicatedBy'],
                    buildingName: cartLineData[`${cartLineIndex}`]['BuildingName'],
                    unitNumber: cartLineData[`${cartLineIndex}`]['UnitNumber'],
                    postalCode: cartLineData[`${cartLineIndex}`]['PostalCode'],
                    birthDate: cartLineData[`${cartLineIndex}`]['BirthDate'],
                    phoneNumber: cartLineData[`${cartLineIndex}`]['PhoneNumber'],
                    schoolName: cartLineData[`${cartLineIndex}`]['SchoolName'],
                    donationOption: cartLineData[`${cartLineIndex}`]['DonationOption'],
                    blessingOption: cartLineData[`${cartLineIndex}`]['BlessingOption'],
                    birthTime: cartLineData[`${cartLineIndex}`]['BirthTime'],
                    lightOption: cartLineData[`${cartLineIndex}`]['LightOption'],
                    validFrom: cartLineData[`${cartLineIndex}`]['ValidFrom'],
                    validTo: cartLineData[`${cartLineIndex}`]['ValidTo'],
                    dedicationPeriod: cartLineData[`${cartLineIndex}`]['DedicationPeriod'],
                    startDate: cartLineData[`${cartLineIndex}`]['StartDate'],
                    gender: cartLineData[`${cartLineIndex}`]['Gender'],
                    country: cartLineData[`${cartLineIndex}`]['Country'],
                    companyName: cartLineData[`${cartLineIndex}`]['CompanyName'],
                    deceasedRelationship: cartLineData[`${cartLineIndex}`]['DeceasedRelationship'],
                    surgeryDate: cartLineData[`${cartLineIndex}`]['SurgeryDate'],
                    deathDate: cartLineData[`${cartLineIndex}`]['DeathDate'],
                    deathTime: cartLineData[`${cartLineIndex}`]['DeathTime'],
                    nameChange: cartLineData[`${cartLineIndex}`]['NameChange'],
                    oldName_1: cartLineData[`${cartLineIndex}`]['OldName_1'],
                    oldName_2: cartLineData[`${cartLineIndex}`]['OldName_2'],
                    birthDate_2: cartLineData[`${cartLineIndex}`]['BirthDate_2'],
                    birthTime_2: cartLineData[`${cartLineIndex}`]['BirthTime_2'],
                    isCustomPrice: found.IsPriceKeyedIn ? found.IsPriceKeyedIn : false,
                    customPriceAmount: found.Price ? found.Price : 0,
                    cartLineId: found.LineId,
                    subProductQuantity: subProductQuantity,
                    chineseName: cartLineData[`${cartLineIndex}`]['ChineseName'],
                    contactNumber: cartLineData[`${cartLineIndex}`]['ContactNumber'],
                    nameNextKin: cartLineData[`${cartLineIndex}`]['NameNextKin'],
                    contactNumberNextKin: cartLineData[`${cartLineIndex}`]['ContactNumberNextKin'],
                    passportNumber: cartLineData[`${cartLineIndex}`]['PassportNumber'],
                    passportExpiryDate: cartLineData[`${cartLineIndex}`]['PassportExpiryDate'],
                    passportIssuedDate: cartLineData[`${cartLineIndex}`]['PassportIssuedDate'],
                    consent: cartLineData[`${cartLineIndex}`]['Consent'],
                    isInsuranceIncluded: cartLineData[`${cartLineIndex}`]['IsInsuranceIncluded']
                });

                this.setState({ isEditMode: true });
            } else {
                this.setState({ isEditMode: false });
            }
        } else if (parameters === '') {
            // Retrieve form inputs if user is logged in.
            const {
                context,
                context: {
                    request: { user }
                }
            } = this.props;

            if (user.customerAccountNumber) {
                let itemInCart = false;

                if (cart.result && product.result) {
                    const cartLines: CartLine[] = cart.result.cart.CartLines!;
                    const foundInCart = cartLines.find(cartline => cartline.ProductId === product.result!.RecordId);

                    if (foundInCart) {
                        itemInCart = true;
                    }
                }

                if (!itemInCart) {
                    let lastOrderCE: any;
                    let lastOrderCommerce: any;
                    let records: string[] = [];

                    // Retrieves all extension table records of this product.
                    if (product.result) {
                        records = await getByCustomerBTGPTransactionAsync(
                            { callerContext: context.actionContext },
                            user.customerAccountNumber,
                            user.emailAddress!,
                            product.result.RecordId.toString(),
                            this.props.config.isUat
                        );

                        if (records.length > 0) {
                            // Filters for records from CE.
                            const recordsCE = records.filter(record => {
                                const input = JSON.parse(record);

                                return input['FromCE'] === true;
                            });

                            // Gets the most recent order from CE of this product.
                            lastOrderCE = recordsCE && recordsCE.length > 0 ? JSON.parse(recordsCE[0]) : undefined;
                        }
                    }

                    const orders = await getOrderHistoryAsync({ callerContext: context.actionContext }, user.customerAccountNumber);

                    if (orders && orders.length > 0) {
                        // Finds the most recent order from Commerce that contains this product.
                        lastOrderCommerce = orders.find((order: SalesOrder) => {
                            const found = records.find(record => {
                                const inputs = JSON.parse(record);

                                return inputs['SalesOrderID'] === order.Id && inputs['FromCE'] === false ? order.Id : '';
                            });

                            return found && found !== '' ? order : undefined;
                        });
                    }

                    let mostRecentOrderID = '';

                    if (lastOrderCE && lastOrderCommerce) {
                        mostRecentOrderID = moment(lastOrderCE['DateOfTrasaction']).isBefore(moment(lastOrderCommerce.CreatedDateTime))
                            ? lastOrderCommerce.Id
                            : lastOrderCE['SalesOrderID'];
                    } else if (lastOrderCE === undefined && lastOrderCommerce) {
                        mostRecentOrderID = lastOrderCommerce.Id;
                    } else if (lastOrderCE && lastOrderCommerce === undefined) {
                        mostRecentOrderID = lastOrderCE['SalesOrderID'];
                    }

                    if (mostRecentOrderID !== '') {
                        const batch = records.filter(record => {
                            const inputs = JSON.parse(record);

                            return inputs['SalesOrderID'] === mostRecentOrderID;
                        });

                        for (let i = 0; i < batch.length; ++i) {
                            const item = JSON.parse(batch[`${i}`]);

                            itemArray.push({
                                comment: '',
                                quantity: item['Quantity'] ? item['Quantity'] : 1,
                                names: [
                                    item['Name_1'],
                                    item['Name_2'],
                                    item['Name_3'],
                                    item['Name_4'],
                                    item['Name_5'],
                                    item['Name_6'],
                                    item['Name_7'],
                                    item['Name_8'],
                                    item['Name_9'],
                                    item['Name_10'],
                                    item['Name_11'],
                                    item['Name_12'],
                                    item['Name_13'],
                                    item['Name_14'],
                                    item['Name_15'],
                                    item['Name_16'],
                                    item['Name_17'],
                                    item['Name_18'],
                                    item['Name_19'],
                                    item['Name_20']
                                ],
                                category: item['Category'],
                                eventID: '',
                                eventName: '',
                                remarks: item['Remarks'],
                                message: item['Message'],
                                dedicatedBy: item['DedicatedBy'],
                                buildingName: item['BuildingName'],
                                unitNumber: item['UnitNumber'],
                                postalCode: item['PostalCode'],
                                birthDate: item['BirthDate'],
                                phoneNumber: item['PhoneNumber'],
                                schoolName: item['SchoolName'],
                                donationOption: item['DonationOption'],
                                blessingOption: item['BlessingOption'],
                                birthTime: item['BirthTime'],
                                lightOption: item['LightOption'],
                                validFrom: item['ValidFrom'],
                                validTo: item['ValidTo'],
                                dedicationPeriod: item['DedicationPeriod'],
                                startDate: item['StartDate'],
                                gender: item['Gender'],
                                country: item['Country'],
                                companyName: item['CompanyName'],
                                deceasedRelationship: item['DeceasedRelationship'],
                                surgeryDate: item['SurgeryDate'],
                                deathDate: item['DeathDate'],
                                deathTime: item['DeathTime'],
                                nameChange: item['NameChange'],
                                oldName_1: item['OldName_1'],
                                oldName_2: item['OldName_2'],
                                birthDate_2: item['BirthDate_2'],
                                birthTime_2: item['BirthTime_2'],
                                isCustomPrice: false,
                                customPriceAmount: 0,
                                cartLineId: '',
                                chineseName: item['ChineseName'],
                                nameNextKin: item['NameNextKin'],
                                contactNumber: item['ContactNumber'],
                                contactNumberNextKin: item['ContactNumberNextKin'],
                                passportNumber: item['PassportNumber'],
                                passportExpiryDate: item['PassportExpiryDate'],
                                passportIssuedDate: item['PassportIssuedDate'],
                                consent: item['Consent'],
                                isInsuranceIncluded: item['IsInsuranceIncluded']
                            });
                        }

                        matchingItemsLength = itemArray.length;
                    }
                }
            }
        }

        if (product && product.result) {
            const resultObj = await this.getProductAttributes(product.result);

            formStructure = getFormJSON(resultObj);

            const formRows = formStructure.formName === 'noOfYearsCategoryNamesForm' ? 1 : matchingItemsLength;

            this.setState({
                eventMinCapacityThreshold: resultObj.eventMinCapacityThreshold,
                allowMultipleRegistrations: resultObj.allowMultipleRegistrations,
                showRemarksField: resultObj.showRemarksField,
                insuranceAmount: resultObj.insuranceAmount,
                insuranceFeeLabel: resultObj.insuranceFeeLabel,
                makeChineseNameMandatory: resultObj.makeChineseNameMandatory
            });
            this.buildFormStructure(formStructure, selectedQuantity, formRows);

            if (resultObj.courseProduct || (resultObj.eventProduct && resultObj.hasCapacityCheck)) {
                await this._checkEventAvailability();
            } else if (resultObj.courseProduct || resultObj.eventProduct) {
                await this._getEventsForProductWithoutAvailability(this.props.data.product.result?.RecordId!);
            } else {
                this.setState({ eventAvailableApiLoaded: true, registrationClosedApiLoaded: true });
            }

            if (resultObj.hasSerial) {
                await this._checkAvailableSerial();
                this.setState({ itemUnavailableSerialApiLoaded: true });
            }

            if (!resultObj.hasSerial) {
                await this._checkAvailableStock();
                this.setState({ itemUnavailableStockApiLoaded: true });
            }

            if (resultObj.subProductRecordId !== 0) {
                void this.getSubProduct(resultObj.subProductRecordId);
            }

            eventProduct = resultObj.eventProduct;
        } else {
            formStructure = getFormJSON({
                formName: 'Offering',
                amounts: [],
                noOfNames: 1,
                companyNames: 1,
                deceasedNames: 1,
                subProductRecordId: 0,
                hasSerial: false,
                eventProduct: false,
                courseProduct: false,
                hasCapacityCheck: false,
                eventMinCapacityThreshold: 0,
                allowMultipleRegistrations: true,
                showRemarksField: true,
                insuranceAmount: '0',
                insuranceFeeLabel: '',
                makeChineseNameMandatory: true
            });

            const formRows = formStructure.formName === 'noOfYearsCategoryNamesForm' ? 1 : matchingItemsLength;

            this.buildFormStructure(formStructure, selectedQuantity, formRows);
        }

        this.setState({ matchingCartItems: itemArray, eventProduct: eventProduct });
    }

    private async getSubProduct(subProductRecordId: number) {
        const data = await getByIdAsync(
            { callerContext: this.props.context.actionContext },
            subProductRecordId,
            this.props.context.actionContext.requestContext.apiSettings.channelId
        );

        this.setState({ subProduct: data });
    }

    // private async getEventProducts(ecommId: number) {
    //   let startDate = new Date();
    //   startDate.setHours(0, 0, 0, 0);

    //   const data = await getProductEventsByIdAsync({ callerContext: this.props.context.actionContext }, this.props.config.isUat, startDate.toISOString(), ecommId.toString());

    //   const events: any = [];

    //   data.map(event => {
    //     const eventJson = JSON.parse(event);

    //     var eventObj: IEvent = {
    //       id: eventJson['EventId'],
    //       name: eventJson['Name'],
    //       startDateTime: eventJson['StartDateTime'],
    //       endDateTime: eventJson['EndDateTime'],
    //       startLunarDate: eventJson['StartLunarDate'],
    //       endLunarDate: eventJson['EndLunarDate']
    //     }

    //     events.push(eventObj);
    //   });

    //   this.setState({ productEvents: events });

    //   this._checkRegistrationPeriod(events);
    // }

    public render(): JSX.Element | null {
        const {
            data: { product }
        } = this.props;

        let customPriceCheck: boolean = false;

        if (product.result?.BasePrice === 0) {
            customPriceCheck = true;
        }

        const ProductDetailsViewProps = {
            ...this.props,
            hasInitOnce: this.state['hasInitOnce'],
            setHasInitOnce: this.state['setHasInitOnce'],
            formScheme: this.state['formStructState'],
            formsHeaders: this.state['headerArrayState'],
            formName: this.state['formName'],
            hasSerial: this.state['hasSerial'],
            registrationClosed: this.state['registrationClosed'],
            itemUnavailable: this.state['itemUnavailable'],
            eventAvailable: this.state['eventAvailable'],
            selectedProduct: product,
            rowsData: this.state['rowsData'],
            addRow: this.state['addRow'],
            replaceRow: this.state['replaceRow'],
            removeRow: this.state['removeRow'],
            removeMultipleRows: this.state['removeMultipleRows'],
            retrieveRow: this.state['retrieveRow'],
            telemetryContent: this.telemetryContent,
            productsList: this.state['productResult'],
            simpleProducts: this.state['simpleProductResult'],
            subProduct: this.state['subProduct'],
            productsIds: this.state['productsIds'],
            productPriceList: this.state['productPriceArr'],
            productAvailabilities: this.state['productsAvailability'],
            matchingCartItems: this.state['matchingCartItems'],
            isCustomPrice: customPriceCheck,
            isEditMode: this.state['isEditMode'],
            eventProduct: this.state['eventProduct'],
            eventRecords: this.state['eventRecords'],
            eventMinCapacityThreshold: this.state['eventMinCapacityThreshold'],
            eventDetails: this.state['eventDetails'],
            allowMultipleRegistrations: this.state['allowMultipleRegistrations'],
            showRemarksField: this.state['showRemarksField'],
            insuranceAmount: this.state['insuranceAmount'],
            insuranceFeeLabel: this.state['insuranceFeeLabel'],
            makeChineseNameMandatory: this.state['makeChineseNameMandatory']
        };

        if (
            this.state['registrationClosedApiLoaded'] ||
            this.state['eventAvailableApiLoaded'] ||
            this.state['itemUnavailableSerialApiLoaded'] ||
            this.state['itemUnavailableStockApiLoaded']
        ) {
            return this.props.renderView(ProductDetailsViewProps);
        } else {
            return null;
        }
        //return this.props.renderView(ProductDetailsViewProps);
    }

    private _checkRegistrationPeriod(productEvents: any[]) {
        if (productEvents.length === 0) {
            this.setState({ registrationClosed: true });
        } else {
            const latestEvent = productEvents[productEvents.length - 1];
            const eventStartDate = moment(latestEvent['startDateTime']);
            const currentTime = moment();

            if (currentTime.diff(eventStartDate, 'd') > 0) {
                this.setState({ registrationClosed: true });
            }
        }
    }

    private async _getEventsForProductWithoutAvailability(ecommId: number) {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        const eventRecords: IEvent[] = [];

        const data = await getProductEventsByIdAsync(
            { callerContext: this.props.context.actionContext },
            this.props.config.isUat,
            startDate.toISOString(),
            ecommId.toString()
        );

        data.map(event => {
            const eventJson = JSON.parse(event);

            const eventObj: IEvent = {
                id: eventJson['EventId'],
                name: eventJson['Name'],
                startDateTime: eventJson['StartDateTime'],
                endDateTime: eventJson['EndDateTime'],
                startLunarDate: eventJson['StartLunarDate'],
                endLunarDate: eventJson['EndLunarDate']
            };

            eventRecords.push(eventObj);
        });

        this._checkRegistrationPeriod(eventRecords);
        this.setState({ eventRecords: eventRecords, registrationClosedApiLoaded: true });
    }

    private async _getEventsForProduct(ecommId: number) {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        const eventRecords: IEvent[] = [];
        const eventDetails: IEventDetails[] = [];

        const data = await getProductEventsByIdAsync(
            { callerContext: this.props.context.actionContext },
            this.props.config.isUat,
            startDate.toISOString(),
            ecommId.toString()
        );

        await Promise.all(
            data.map(async event => {
                const eventJson = JSON.parse(event);

                const checkEventAvailableResult = await getEventAvailabilityAsync(
                    { callerContext: this.props.context.actionContext },
                    this.props.config.isUat,
                    eventJson['EventId']
                );

                if (checkEventAvailableResult && checkEventAvailableResult[0] !== '') {
                    const eventAvailabiltyDetails = JSON.parse(checkEventAvailableResult[0]);

                    if (eventAvailabiltyDetails.isEventAvailable === true) {
                        const remainingSlots = eventAvailabiltyDetails.maxEventCapacity - eventAvailabiltyDetails.registrationCount;
                        const hideAddMoreButton = remainingSlots > this.state['eventMinCapacityThreshold'] ? false : true;
                        const remainingEcommSlots = remainingSlots > this.state['eventMinCapacityThreshold'] ? remainingSlots : 0;

                        const eventObj: IEvent = {
                            id: eventJson['EventId'],
                            name: eventJson['Name'],
                            startDateTime: eventJson['StartDateTime'],
                            endDateTime: eventJson['EndDateTime'],
                            startLunarDate: eventJson['StartLunarDate'],
                            endLunarDate: eventJson['EndLunarDate']
                        };

                        const eventDetailsObj: IEventDetails = {
                            id: eventJson['EventId'],
                            name: eventJson['Name'],
                            startDateTime: eventJson['StartDateTime'],
                            endDateTime: eventJson['EndDateTime'],
                            startLunarDate: eventJson['StartLunarDate'],
                            endLunarDate: eventJson['EndLunarDate'],
                            hideAddMoreButton: hideAddMoreButton,
                            eventAvailableSlot: remainingEcommSlots
                        };

                        eventRecords.push(eventObj);
                        eventDetails.push(eventDetailsObj);
                    }
                }
            })
        );

        this._checkRegistrationPeriod(eventRecords);
        this.setState({ eventRecords: eventRecords, eventDetails: eventDetails, registrationClosedApiLoaded: true });

        return eventRecords;
    }
    private async _checkEventAvailability() {
        let eventAvailable = false;
        const eventIdResult = await this._getEventsForProduct(this.props.data.product.result?.RecordId!);

        if (eventIdResult.length > 0) {
            eventAvailable = true;

            this.setState({ eventAvailable: eventAvailable });
        }

        this.setState({ eventAvailableApiLoaded: true });

        return eventAvailable;
    }

    private async _checkAvailableStock() {
        let haveStock = true;
        const productId = (await this.props.data.product).RecordId;

        const availability = await getEstimatedAvailabilityAsync(
            { callerContext: this.props.context.actionContext },
            { ProductIds: [productId], DefaultWarehouseOnly: true }
        ).catch(error => console.log('[_checkAvailableStock] getEstimatedAvailabilityAsync error: ', error));

        if (availability && availability.ProductWarehouseInventoryAvailabilities) {
            const warehouse = availability.ProductWarehouseInventoryAvailabilities;

            if (warehouse.length > 0) {
                const found = warehouse.find(stock => stock.ProductId === productId);

                if (
                    found &&
                    found.PhysicalAvailableInventoryLevelCode &&
                    found.PhysicalAvailableInventoryLevelCode !== InventoryLvls.AVAIL
                ) {
                    haveStock = false;

                    this.setState({ itemUnavailable: true });
                }
            }
        }

        return haveStock;
    }

    private async _checkAvailableSerial() {
        let haveSerial = true;

        const checkAvailableSerialResult = await checkAvailableSerialAsync(
            { callerContext: this.props.context.actionContext },
            this.props.config.isUat,
            this.props.data.product.result?.RecordId.toString()!
        );

        if (checkAvailableSerialResult && typeof checkAvailableSerialResult === 'object' && checkAvailableSerialResult['value'] === false) {
            haveSerial = false;

            this.setState({ itemUnavailable: true });
        }

        return haveSerial;
    }

    private buildFormStructure(
        formStructure: IFormStructure,
        selectedQuantity: AsyncResult<IGetSelectedQuantityStateData>,
        matchingItemsLength: number
    ) {
        const { context } = this.props;

        this.tableHeaderArray = [];

        formStructure.fields?.map((element: any) => {
            this.tableHeaderArray.push(element.field_label);
        });

        const addRowAction = (noOfRows: number) => {
            context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                selectedQuantity: Number(this.state['rowsData']?.length ?? 0) + Number(noOfRows ?? 0)
            });

            const rowsDataArray = this.state['rowsData'].slice();
            for (let i = 0; i < noOfRows; i++) {
                rowsDataArray.push({ id: uuidv4() });
            }
            this.setState({ rowsData: rowsDataArray });
        };
        this.setState({ addRow: addRowAction });

        const replaceRowAction = (noOfRows: number, idx: number[]) => {
            context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                selectedQuantity: Number(this.state['rowsData']?.length ?? 0) + Number(noOfRows ?? 0)
            });

            const rowsDataArray = this.state['rowsData'].slice();
            for (let i = 0; i < noOfRows; i++) {
                rowsDataArray.push({ id: uuidv4() });
            }
            this.setState({ rowsData: rowsDataArray }, () => removeMultipleRowsAction(idx));
        };
        this.setState({ replaceRow: replaceRowAction });

        const removeRowAction = (index: number) => {
            if (this.state['rowsData'].length > 1) {
                context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                    selectedQuantity: this.state['rowsData'].length - 1
                });

                this.setState({
                    rowsData: [...this.state['rowsData'].slice(0, index), ...this.state['rowsData'].slice(index + 1)]
                });
            } else {
                this.setState({ isEditMode: false });
            }
        };

        this.setState({ removeRow: removeRowAction });

        const removeMultipleRowsAction = (index: number[]) => {
            if (this.state['rowsData'].length > 1) {
                context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                    selectedQuantity: this.state['rowsData'].length - 1
                });

                const stateData = [...this.state['rowsData']];
                for (let i = 0; i < index.length; i++) {
                    stateData.splice(index[`${i}`], 1);
                }

                this.setState({ rowsData: [...stateData] });
            } else {
                this.setState({ isEditMode: false });
            }
        };

        this.setState({ removeMultipleRows: removeMultipleRowsAction });

        const retrieveRowsAction = (input: any[]) => {
            if (input.length !== 0) {
                const ids = [];

                for (let i = 0; i < input.length; ++i) {
                    ids.push({ id: input[`${i}`].DraftID });
                }

                context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                    selectedQuantity: ids.length
                });

                this.setState({ rowsData: ids });
            }
        };

        this.setState({ retrieveRow: retrieveRowsAction });

        if (this.tableHeaderArray.length > 0 && this.state['headerArrayState'].length !== this.tableHeaderArray.length) {
            this.setState({ headerArrayState: this.tableHeaderArray });
        }

        if (
            formStructure &&
            formStructure.fields &&
            formStructure.fields.length > 0 &&
            formStructure.fields.length !== this.state['formStructState'].length
        ) {
            this.setState({ formStructState: formStructure.fields });
        }

        if (formStructure && formStructure.formName && formStructure.formName !== this.state['formName']) {
            this.setState({ formName: formStructure.formName });
        }

        if (formStructure && formStructure.hasSerial && formStructure.hasSerial !== this.state['hasSerial']) {
            this.setState({ hasSerial: formStructure.hasSerial });
        }

        if (
            !this.state['isEditMode'] &&
            selectedQuantity.result &&
            matchingItemsLength > 0 &&
            matchingItemsLength !== this.state['rowsData'].length
        ) {
            if (matchingItemsLength > this.state['rowsData'].length) {
                const difference = matchingItemsLength - this.state['rowsData'].length;

                for (let i = 0; i < difference; i++) {
                    this.setState({
                        rowsData: [...this.state['rowsData'], { id: uuidv4() }]
                    });
                }

                context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                    selectedQuantity: selectedQuantity.result.selectedQuantity
                });
            }

            if (matchingItemsLength < this.state['rowsData'].length) {
                const difference = this.state['rowsData'].length - matchingItemsLength;

                const index = this.state['rowsData'].length - 1;

                for (let j = 0; j < difference; j++) {
                    this.setState({
                        rowsData: [...this.state['rowsData'].slice(0, index), ...this.state['rowsData'].slice(index + 1)]
                    });
                }

                context.actionContext.update(new GetSelectedQuantityValuesInput(), {
                    selectedQuantity: selectedQuantity.result.selectedQuantity
                });
            }
        }
    }

    private async getProductAttributes(product: SimpleProduct): Promise<Attributes> {
        const { context } = this.props;

        const attributesList = await getAttributeValuesAsync(
            { callerContext: context.actionContext },
            product.RecordId,
            context.request.apiSettings.channelId,
            context.request.apiSettings.catalogId
        );

        let formName = '';
        let amountArray: string[] = [];
        let names: number = 1;
        let companyNames: number = 1;
        let deceasedNames: number = 1;
        let subProductRecordId: number = 0;
        let hasSerial: boolean = false;
        let eventProduct: boolean = true;
        let courseProduct: boolean = false;
        let hasCapacityCheck: boolean = false;
        let eventMinCapacityThreshold: number = 0;
        let allowMultipleEventRegistrations: boolean = true;
        let showRemarksField: boolean = true;
        let insuranceAmount: string = '0';
        let insuranceFeeLabel: string = '';
        let makeChineseNameMandatory: boolean = true;

        for (let i = 0; i < attributesList.length; i++) {
            if (attributesList[`${i}`].Name?.includes('Form')) {
                formName = attributesList[`${i}`].TextValue ? attributesList[`${i}`].TextValue! : 'Offering';
            } else if (attributesList[`${i}`].Name?.includes('Donation Amount')) {
                const amount = attributesList[`${i}`].TextValue ? attributesList[`${i}`].TextValue! : '2,5,10,50';

                amountArray = amount.split(',');
                amountArray.push(resource.otherLabel[this.localeCode as 'en' | 'zh']);
                /* if (this.localeCode === resource.otherLabel.en) {
                    amountArray.push(resource.otherLabel.en);
                } else {
                    amountArray.push(resource.otherLabel.zh);
                } */
            } else if (attributesList[`${i}`].Name?.includes('No Of Names')) {
                names = attributesList[`${i}`].IntegerValue ? attributesList[`${i}`].IntegerValue! : 1;
            } else if (attributesList[`${i}`].Name?.includes('Company Names')) {
                companyNames = attributesList[`${i}`].IntegerValue ? attributesList[`${i}`].IntegerValue! : 1;
            } else if (attributesList[`${i}`].Name?.includes('Deceased Names')) {
                deceasedNames = attributesList[`${i}`].IntegerValue ? attributesList[`${i}`].IntegerValue! : 1;
            } else if (attributesList[`${i}`].Name?.includes('Sub Product Record ID')) {
                subProductRecordId = attributesList[`${i}`].TextValue ? +attributesList[`${i}`].TextValue! : 0;
            } else if (attributesList[`${i}`].Name?.includes('Has Serial')) {
                hasSerial = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            } else if (attributesList[`${i}`].Name?.includes('EventProduct')) {
                eventProduct = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            } else if (attributesList[`${i}`].Name?.includes('CourseProduct')) {
                courseProduct = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            } else if (attributesList[`${i}`].Name?.includes('HasCapacityCheck')) {
                hasCapacityCheck = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            } else if (attributesList[`${i}`].Name?.includes('EventMinCapacityThreshold')) {
                eventMinCapacityThreshold = attributesList[`${i}`].IntegerValue ? attributesList[`${i}`].IntegerValue! : 0;
            } else if (attributesList[`${i}`].Name?.includes('AllowMultipleEventRegistrations')) {
                allowMultipleEventRegistrations = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            } else if (attributesList[`${i}`].Name?.includes('ShowRemarksField')) {
                showRemarksField = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            } else if (attributesList[`${i}`].Name?.includes('InsuranceAmount')) {
                insuranceAmount = attributesList[`${i}`].TextValue ? attributesList[`${i}`].TextValue! : '0';
            } else if (attributesList[`${i}`].Name?.includes('InsuranceFeeLabel')) {
                insuranceFeeLabel = attributesList[`${i}`].TextValue ? attributesList[`${i}`].TextValue! : '0';
            } else if (attributesList[`${i}`].Name?.includes('MakeChineseNameMandatory')) {
                makeChineseNameMandatory = attributesList[`${i}`].BooleanValue ? attributesList[`${i}`].BooleanValue! : false;
            }
        }

        return {
            formName: formName,
            amounts: amountArray,
            noOfNames: names,
            companyNames: companyNames,
            deceasedNames: deceasedNames,
            subProductRecordId: subProductRecordId,
            hasSerial: hasSerial,
            eventProduct: eventProduct,
            courseProduct: courseProduct,
            hasCapacityCheck: hasCapacityCheck,
            eventMinCapacityThreshold: eventMinCapacityThreshold,
            allowMultipleRegistrations: allowMultipleEventRegistrations,
            showRemarksField: showRemarksField,
            insuranceAmount: insuranceAmount,
            insuranceFeeLabel: insuranceFeeLabel,
            makeChineseNameMandatory: makeChineseNameMandatory
        };
    }
}

export default EcommProductDetailsForm;
